import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  MenuBoxIcon,
  MenuCloseIcon,
  MenuPlusIcon,
  Spinner,
} from '@noor/ui';
import { toast, useIsMobile } from '@noor/lib';
import { cn } from '@noor/lib/cn';
import { getApiErrorMessage } from '@noor/api';
import { useBranchProducts } from '@/entities/menu';
import { promotionApi, promotionKeys, type Promotion } from '@/entities/promotion';
import type { BranchSelections, PromotionBranch } from '../model/schema';
import { BranchProductStep } from './branch-product-step';

interface AddPromotionProductsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (() => void) | undefined;
  promotionId: string;
  branches: PromotionBranch[];
  initialBranchId?: string | undefined;
  existingItems?: Promotion[] | undefined;
}

export function AddPromotionProductsModal({
  open,
  onClose,
  onSuccess,
  promotionId,
  branches,
  initialBranchId,
  existingItems,
}: AddPromotionProductsModalProps) {
  const { t } = useTranslation('eats');
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const [activeBranchId, setActiveBranchId] = useState<string | undefined>(
    () => initialBranchId ?? branches[0]?.id,
  );
  const [selections, setSelections] = useState<BranchSelections>({});
  const [isPending, setIsPending] = useState(false);

  const { data: branchProducts, isPending: branchProductsPending } =
    useBranchProducts(activeBranchId);

  const disabledProductIds = useMemo(() => {
    if (!existingItems || !activeBranchId) return undefined;
    return existingItems
      .filter((item) => item.restaurantId === activeBranchId)
      .map((item) => String(item.productId));
  }, [existingItems, activeBranchId]);

  const selectedEntries = Object.entries(selections).filter(([, ids]) => ids.length > 0);
  const selectedCount = selectedEntries.reduce((sum, [, ids]) => sum + ids.length, 0);

  async function handleSubmit(): Promise<void> {
    if (selectedCount === 0 || isPending) return;
    setIsPending(true);
    try {
      await Promise.all(
        selectedEntries.map(([branchId, productIds]) =>
          promotionApi.attachProducts(promotionId, productIds, branchId),
        ),
      );
      toast.success(t('promotions.addProductsSuccess'));
      onSuccess?.();
      void queryClient.invalidateQueries({
        queryKey: promotionKeys.all,
      });
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('promotions.form.error')));
    } finally {
      setIsPending(false);
    }
  }

  const title = t('promotions.form.addProductsTitle');
  const subtitle = t('promotions.form.addProductsSubtitle');

  const content = (
    <BranchProductStep
      branches={branches}
      products={branchProducts ?? []}
      value={selections}
      onChange={setSelections}
      activeBranchId={activeBranchId}
      onSelectBranch={setActiveBranchId}
      isLoading={branchProductsPending}
      disabledProductIds={disabledProductIds}
    />
  );

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next: boolean) => (next ? undefined : onClose())}
      >
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
            )}
          >
            {/* Drag handle */}
            <div className="flex shrink-0 justify-center pb-2">
              <div className="h-1 w-10 rounded-sm bg-muted" />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-t-[2rem] bg-card p-4">
              <div className="flex items-center justify-between">
                <DialogTitle className="px-1 text-[1.375rem] font-semibold tracking-tight text-foreground">
                  {title}
                </DialogTitle>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('common.close')}
                  className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
                >
                  <MenuCloseIcon className="size-5" />
                </button>
              </div>
              <DialogDescription className="sr-only">{subtitle}</DialogDescription>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {content}
              </div>

              <div className="flex shrink-0 items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isPending}
                  className="h-12 flex-1 rounded-full text-base font-semibold"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={isPending || selectedCount === 0}
                  className="h-12 flex-1 rounded-full text-base font-semibold"
                >
                  {isPending ? (
                    <Spinner className="size-[18px]" />
                  ) : (
                    <MenuPlusIcon className="size-[18px]" />
                  )}
                  {t('common:actions.add')}
                  {selectedCount > 0 && ` (${selectedCount})`}
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => (next ? undefined : onClose())}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className={cn(
            'grid max-h-[calc(100dvh-4rem)] w-[48.8rem] max-w-[calc(100vw-2rem)] ' +
              'grid-rows-[auto_minmax(0,1fr)_auto] gap-0 rounded-3xl bg-muted p-5 sm:max-w-[48.8rem] ' +
              '[&>[data-slot=dialog-header]]:border-b [&>[data-slot=dialog-header]]:border-border ' +
              '[&>[data-slot=dialog-header]]:pr-12 [&>[data-slot=dialog-header]]:pb-5',
          )}
          showClose={!isPending}
        >
          <DialogHeader>
            <DialogTitle asChild>
              <span className="flex items-center gap-4">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-xs">
                  <MenuBoxIcon className="size-6" aria-hidden />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-[1.375rem] font-semibold tracking-tight text-foreground">
                    {title}
                  </span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {subtitle}
                  </span>
                </span>
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">{subtitle}</DialogDescription>
          </DialogHeader>

          <div className="-mx-1 flex h-[28rem] max-h-[60dvh] min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-1 pt-5">
            {content}
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="h-12 min-w-[9rem] rounded-full border-transparent bg-border text-base font-semibold text-foreground shadow-none hover:bg-border/70"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={isPending || selectedCount === 0}
              className="h-12 min-w-[11.125rem] rounded-full text-base font-semibold"
            >
              {isPending ? (
                <Spinner className="size-[18px]" />
              ) : (
                <MenuPlusIcon className="size-[18px]" />
              )}
              {t('common:actions.add')}
              {selectedCount > 0 && ` (${selectedCount})`}
            </Button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
